import {
  Component,
  computed,
  effect,
  inject,
  TemplateRef,
  ViewChild,
  OnInit,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { EquipmentStore } from '@features/catalog/store';
import { CurrentOrganizationStore } from '@features/organizations/store/current-organization.store';
import {
  DataTableComponent,
  ColumnDef,
  TableAction,
} from '@shared/components/data-table/data-table';
import { SolarStorage } from '@features/catalog/models';
import { CreateStorageDialogComponent } from '@shared/components/create-storage-dialog/create-storage-dialog';

@Component({
  selector: 'app-storages-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    DataTableComponent,
    CreateStorageDialogComponent,
  ],
  templateUrl: './storages-list.html',
  styleUrl: './storages-list.css',
})
export class StoragesListComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  readonly queryParams = toSignal(this.route.queryParamMap, { initialValue: this.route.snapshot.queryParamMap });
  readonly linkedEquipment = computed(() => this.queryParams().get('equipment'));
  readonly visibleEquipment = computed(() => {
    const id = this.linkedEquipment();
    const items = this.equipmentStore.allStorages();
    return id ? items.filter(item => item.id === id) : items;
  });
  private readonly equipmentStore = inject(EquipmentStore);
  readonly currentOrgStore = inject(CurrentOrganizationStore);

  @ViewChild('createDialog') createDialog!: CreateStorageDialogComponent;
  @ViewChild('storageTpl', { static: true }) storageTpl!: TemplateRef<any>;
  @ViewChild('capacityTpl', { static: true }) capacityTpl!: TemplateRef<any>;
  @ViewChild('priceTpl', { static: true }) priceTpl!: TemplateRef<any>;

  // State for edit mode
  editingStorage = signal<SolarStorage | null>(null);

  organizationId = computed(() => this.currentOrgStore.organizationId() || '');
  storages = computed(() => this.equipmentStore.allStorages());
  isLoading = computed(() => this.equipmentStore.isLoading());

  columns: ColumnDef<SolarStorage>[] = [];

  ngOnInit() {
    this.columns = [
      {
        header: 'Speicher',
        template: this.storageTpl,
      },
      {
        header: 'Kapazität',
        template: this.capacityTpl,
        class: 'w-[130px] text-right',
      },
      {
        header: 'Preis',
        template: this.priceTpl,
        class: 'w-[140px] text-right',
      },
    ];
  }

  actions: TableAction<SolarStorage>[] = [
    {
      label: 'Bearbeiten',
      action: (item) => this.editStorage(item),
      visible: (item) => !item.isGlobal && this.currentOrgStore.can('equipment', 'update'),
    },
    {
      label: 'Löschen',
      type: 'destructive',
      action: (item) => this.deleteStorage(item),
      visible: (item) => !item.isGlobal && this.currentOrgStore.can('equipment', 'delete'),
      dividerBefore: true,
    },
  ];

  constructor() {
    // Legitimate side-effect use: triggers a store load (network I/O) whenever
    // the active organisation changes. This is not state derivation — it is an
    // imperative command to fetch data. No cleaner alternative exists here because
    // the load must re-fire reactively on orgId changes without an Observable pipeline.
    effect(() => {
      const orgId = this.organizationId();
      if (orgId) {
        this.equipmentStore.loadStorages(orgId);
      }
    });
  }

  async syncStorages() {
    try {
      const orgId = this.organizationId();
      if (orgId) {
        await this.equipmentStore.syncStorages(orgId);
      }
    } catch (error) {
      console.error('Error syncing storages:', error);
    }
  }

  addStorage() {
    this.editingStorage.set(null);
    this.createDialog?.open();
  }

  editStorage(storage: SolarStorage) {
    console.log('Edit storage:', storage);
    this.editingStorage.set(storage);
    this.createDialog?.open();
  }

  async deleteStorage(storage: SolarStorage) {
    if (
      confirm(`Möchten Sie den Speicher "${storage.name}" wirklich löschen?`)
    ) {
      try {
        const orgId = this.organizationId();
        if (orgId) {
          await this.equipmentStore.deleteStorage(orgId, storage.id);
        }
      } catch (error) {
        console.error('Error deleting storage:', error);
      }
    }
  }
}
